import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { apiRequest } from '../lib/api-client';
import { formatDateTime } from '../ui/format';
import { Badge, ErrorState, Loading, Panel, type BadgeTone } from '../ui/Page';

/**
 * What customers said, and what was done about it (Phase 129).
 *
 * The reports arrive from every tenant, so this is the one screen that reads across all of them —
 * and the reason the table is platform data rather than tenant data. Triage is deliberately four
 * words and a note: anything more and nobody keeps it up, and a queue nobody keeps up is worse
 * than none, because it makes the product look like it is listening when it is not.
 */

type Status = 'NEW' | 'READ' | 'PLANNED' | 'DONE' | 'DECLINED';

interface Row {
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  userName: string | null;
  userEmail: string | null;
  kind: 'PROBLEM' | 'IDEA';
  message: string;
  page: string | null;
  userAgent: string | null;
  status: Status;
  note: string | null;
  createdAt: string;
}

const STATUSES: Status[] = ['NEW', 'READ', 'PLANNED', 'DONE', 'DECLINED'];

const tones: Record<Status, BadgeTone> = {
  NEW: 'info',
  READ: 'neutral',
  PLANNED: 'warning',
  DONE: 'positive',
  DECLINED: 'neutral',
};

export function FeedbackPanel(): React.JSX.Element {
  const client = useQueryClient();
  const [filter, setFilter] = useState<Status | 'ALL'>('NEW');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['admin-feedback', filter],
    queryFn: () =>
      apiRequest<{ feedback: Row[] }>(
        `/admin/feedback${filter === 'ALL' ? '' : `?status=${filter}`}`,
      ),
  });

  const triage = useMutation({
    mutationFn: (input: { id: string; status: Status }) =>
      apiRequest(`/admin/feedback/${input.id}`, {
        method: 'PATCH',
        body: { status: input.status },
      }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['admin-feedback'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (list.isLoading) return <Loading />;
  if (list.isError) return <ErrorState error={list.error} />;

  const rows = list.data?.feedback ?? [];

  return (
    <Panel title={t('admin.feedback.title')} description={t('admin.feedback.description')}>
      <div className="chips" role="tablist">
        {(['ALL', ...STATUSES] as (Status | 'ALL')[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            className={`chip${filter === key ? ' chip--active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {key === 'ALL' ? t('admin.feedback.all') : t(`admin.feedback.status.${key}`)}
          </button>
        ))}
      </div>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {rows.length === 0 ? (
        <p className="muted">{t('admin.feedback.empty')}</p>
      ) : (
        <ul className="feedback-list">
          {rows.map((row) => (
            <li key={row.id} className="feedback-item">
              <div className="cell-inline">
                <Badge tone={row.kind === 'PROBLEM' ? 'danger' : 'info'}>
                  {t(`report.kind.${row.kind}`)}
                </Badge>
                <Badge tone={tones[row.status]}>{t(`admin.feedback.status.${row.status}`)}</Badge>
                <span className="muted small">{formatDateTime(row.createdAt)}</span>
              </div>

              <p className="feedback-item__message">{row.message}</p>

              {/* Who and where, which is what turns a sentence into something to go and look at. */}
              <p className="muted small">
                {row.userName ?? '—'}
                {row.userEmail ? ` · ${row.userEmail}` : ''}
                {row.tenantName ? ` · ${row.tenantName}` : ''}
                {row.page ? ` · ${row.page}` : ''}
              </p>

              {row.note && <p className="small">{row.note}</p>}

              <div className="chips">
                {STATUSES.filter((status) => status !== row.status).map((status) => (
                  <button
                    key={status}
                    type="button"
                    className="chip"
                    disabled={triage.isPending}
                    onClick={() => triage.mutate({ id: row.id, status })}
                  >
                    {t(`admin.feedback.status.${status}`)}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
